import { useCallback, useMemo } from 'react'

import { IconType } from '@factorialco/f0-react'
import { Folder, Reset } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import {
  ActionHandlers,
  ModalHandlers,
  NavigationHandler,
} from 'modules/expenses/components/expenses/DataCollection/bulkActions/actionCommands'
import {
  ACTION_IDS,
  ActionId,
} from 'modules/expenses/components/expenses/DataCollection/bulkActions/determineAvailableActions'
import { Expensable } from 'modules/expenses/components/expenses/DataCollection/bulkActions/types'
import { Section } from 'modules/expenses/lib/RouteContextParams'
import {
  canAddExpensableToPayment,
  canAddExpensableToPayroll,
  canRemoveExpensableFromPayroll,
  canRestartExpensable,
  canRevertPaidExpensable,
  canReviewExpensable,
  canSetToPaidExpensable,
} from 'modules/expenses/lib/expensables/availableActions'

interface UseExpenseItemActionsProps {
  canSyncWithPayroll: boolean
  canSyncWithBanking: boolean
  canRestartApprovalFlow: boolean
  canSetExpensableToPaid: boolean
  section?: Section
  handlers: ActionHandlers
  modalActions: ModalHandlers
  navigationHandler: NavigationHandler
  isViewingGroup?: boolean
}

type ItemAction =
  | {
      id: ActionId
      label: string
      icon?: IconType
      onClick: () => void
      enabled?: boolean
      type?: 'primary' | 'secondary' | 'other'
      critical?: boolean
      description?: string
    }
  | {
      type: 'separator'
    }

const computeItemEligibility = (
  expensable: Expensable,
  permissions: {
    canSyncWithPayroll: boolean
    canSyncWithBanking: boolean
    canRestartApprovalFlow: boolean
    canSetExpensableToPaid: boolean
    section?: Section
    isViewingGroup?: boolean
  }
) => {
  return {
    canApprove: canReviewExpensable(expensable, permissions.section),
    canReject: canReviewExpensable(expensable, permissions.section),
    canReimbursePayroll: canAddExpensableToPayroll(expensable, permissions.canSyncWithPayroll),
    canReimburseSepa: canAddExpensableToPayment(expensable, permissions.canSyncWithBanking),
    canSetPaid: canSetToPaidExpensable(
      expensable,
      permissions.canSetExpensableToPaid,
      permissions.section
    ),
    canResetApproval: canRestartExpensable(
      expensable,
      permissions.canRestartApprovalFlow,
      permissions.section
    ),
    canRemovePayroll: canRemoveExpensableFromPayroll(expensable, permissions.canSyncWithPayroll),
    canRevertPaid: canRevertPaidExpensable(
      expensable,
      permissions.canSetExpensableToPaid,
      permissions.section
    ),
    canViewGroup: !!expensable.groupId && !permissions.isViewingGroup,
  }
}

/**
 * Maps eligibility flags to UI action objects.
 * Returns actions in logical order with proper grouping via separators.
 *
 * Action structure:
 * - Primary approval actions (approve)
 * - Secondary approval actions (reject)
 * - Separator
 * - Reimbursement actions (payroll, SEPA, set paid)
 * - Separator (if reset/cleanup actions exist)
 * - Reset/cleanup actions (reset approval, remove from payroll, revert paid)
 */
const buildItemActions = (
  expensable: Expensable,
  eligibility: ReturnType<typeof computeItemEligibility>,
  executeAction: (actionId: ActionId, expensable: Expensable) => Promise<void>
): ItemAction[] => {
  const actions: ItemAction[] = []
  let hasApprovalActions = false
  let hasReimbursementActions = false

  // Approval workflow - Primary action
  if (eligibility.canApprove) {
    actions.push({
      id: ACTION_IDS.APPROVE,
      label: i18n.t('expenses_v2.list.actions.approve'),
      type: 'primary',
      onClick: () => executeAction(ACTION_IDS.APPROVE, expensable),
    })
    hasApprovalActions = true
  }

  // Approval workflow - Secondary action
  if (eligibility.canReject) {
    actions.push({
      id: ACTION_IDS.REJECT,
      label: i18n.t('expenses_v2.list.actions.reject'),
      type: 'secondary',
      onClick: () => executeAction(ACTION_IDS.REJECT, expensable),
    })
    hasApprovalActions = true
  }

  // Separator between approval and reimbursement
  if (
    hasApprovalActions &&
    (eligibility.canReimbursePayroll || eligibility.canReimburseSepa || eligibility.canSetPaid)
  ) {
    actions.push({ type: 'separator' })
  }

  // Reimbursement workflow
  // Design decision: Match bulk actions UX - show 'Reimburse' as primary,
  // detailed options ('via payroll'/'via SEPA') appear on expand (three dots)
  const hasPayroll = eligibility.canReimbursePayroll
  const hasSepa = eligibility.canReimburseSepa
  const hasAnyReimbursement = hasPayroll || hasSepa

  if (hasAnyReimbursement) {
    // Always show "Reimburse" as primary, regardless of which method is available
    // This ensures consistent UX whether user has payroll, SEPA, or both
    const primaryActionId = hasPayroll ? ACTION_IDS.REIMBURSE_PAYROLL : ACTION_IDS.REIMBURSE_SEPA

    actions.push({
      id: primaryActionId,
      label: i18n.t('expenses_v2.list.actions.reimburse'),
      type: 'primary',
      onClick: () => executeAction(primaryActionId, expensable),
    })

    // Expanded actions: Show detailed reimbursement options
    if (hasPayroll) {
      actions.push({
        id: ACTION_IDS.REIMBURSE_PAYROLL,
        label: i18n.t('expenses_v2.list.actions.reimburse_through_payroll'),
        onClick: () => executeAction(ACTION_IDS.REIMBURSE_PAYROLL, expensable),
      })
    }

    if (hasSepa) {
      actions.push({
        id: ACTION_IDS.REIMBURSE_SEPA,
        label: i18n.t('expenses_v2.list.actions.reimburse_through_sepa_file'),
        onClick: () => executeAction(ACTION_IDS.REIMBURSE_SEPA, expensable),
      })
    }

    hasReimbursementActions = true
  }

  if (eligibility.canSetPaid) {
    actions.push({
      id: ACTION_IDS.SET_PAID,
      label: i18n.t('expenses_v2.list.actions.set_as_paid'),
      onClick: () => executeAction(ACTION_IDS.SET_PAID, expensable),
    })
    hasReimbursementActions = true
  }

  // Separator between reimbursement and reset/cleanup actions
  if (
    (hasApprovalActions || hasReimbursementActions) &&
    (eligibility.canResetApproval || eligibility.canRemovePayroll || eligibility.canRevertPaid)
  ) {
    actions.push({ type: 'separator' })
  }

  // Reset workflow
  if (eligibility.canResetApproval) {
    actions.push({
      id: ACTION_IDS.RESET_APPROVAL,
      label: i18n.t('expenses_v2.list.actions.reset_approval_flow'),
      icon: Reset,
      type: 'secondary',
      critical: true,
      onClick: () => executeAction(ACTION_IDS.RESET_APPROVAL, expensable),
    })
  }

  // Cleanup actions - potentially destructive, marked as critical
  if (eligibility.canRemovePayroll) {
    actions.push({
      id: ACTION_IDS.REMOVE_FROM_PAYROLL,
      label: i18n.t('expenses_v2.list.actions.remove_from_payroll'),
      critical: true,
      onClick: () => executeAction(ACTION_IDS.REMOVE_FROM_PAYROLL, expensable),
    })
  }

  if (eligibility.canRevertPaid) {
    actions.push({
      id: ACTION_IDS.REVERT_PAID,
      label: i18n.t('expenses_v2.buttons.revert_paid'),
      critical: true,
      onClick: () => executeAction(ACTION_IDS.REVERT_PAID, expensable),
    })
  }

  if (eligibility.canViewGroup) {
    actions.push({
      id: ACTION_IDS.VIEW_GROUP,
      label: i18n.t('expenses_v2.list.actions.view_group'),
      type: 'secondary',
      icon: Folder,
      onClick: () => executeAction(ACTION_IDS.VIEW_GROUP, expensable),
    })
  }

  return actions
}

export const useExpenseItemActions = ({
  canSyncWithPayroll,
  canSyncWithBanking,
  canRestartApprovalFlow,
  canSetExpensableToPaid,
  section,
  handlers,
  modalActions,
  navigationHandler,
  isViewingGroup = false,
}: UseExpenseItemActionsProps) => {
  const permissions = useMemo(
    () => ({
      canSyncWithPayroll,
      canSyncWithBanking,
      canRestartApprovalFlow,
      canSetExpensableToPaid,
      section,
      isViewingGroup,
    }),
    [
      canSyncWithPayroll,
      canSyncWithBanking,
      canRestartApprovalFlow,
      canSetExpensableToPaid,
      section,
      isViewingGroup,
    ]
  )

  const executeAction = useCallback(
    async (actionId: ActionId, expensable: Expensable) => {
      const id = expensable.id

      switch (actionId) {
        case ACTION_IDS.APPROVE:
          await handlers.onApprove([id])
          break

        case ACTION_IDS.REJECT:
          modalActions.openRejectModal([expensable])
          break

        case ACTION_IDS.REIMBURSE_PAYROLL:
          modalActions.openPayrollModal([expensable])
          break

        case ACTION_IDS.REIMBURSE_SEPA:
          modalActions.openPaymentModal([expensable])
          break

        case ACTION_IDS.SET_PAID:
          await handlers.onSetToPaid([id])
          break

        case ACTION_IDS.RESET_APPROVAL:
          await handlers.onRestartReview([id])
          break

        case ACTION_IDS.REMOVE_FROM_PAYROLL:
          await handlers.onRemoveFromPayroll([id])
          break

        case ACTION_IDS.REVERT_PAID:
          await handlers.onRevertPaid([id])
          break

        case ACTION_IDS.VIEW_GROUP:
          if (!expensable.groupId) return

          const { navigate, navigation } = navigationHandler
          navigate(navigation.helpers.groupDetail(expensable.groupId))
          break
      }
    },
    [handlers, modalActions, navigationHandler]
  )

  const getItemActions = useMemo(
    () => (expensable: Expensable) => {
      const eligibility = computeItemEligibility(expensable, permissions)
      return buildItemActions(expensable, eligibility, executeAction)
    },
    [permissions, executeAction]
  )

  return {
    getItemActions,
  }
}
